import { css } from "@emotion/react";

/**
 * CSS Variable names for tenant-configurable colors
 * These are set at runtime from the widget config API response
 */
export const CSS_VAR_NAMES = {
  textColor: "--pollen-text-color",
  backgroundColor: "--pollen-background-color",
  primaryColor: "--pollen-primary-color",
  cardBackgroundColor: "--pollen-card-background-color",
  textSecondaryColor: "--pollen-text-secondary-color",
  borderColor: "--pollen-border-color",
  accentColor: "--pollen-accent-color",
  fontFamily: "--pollen-font-family",
  headingFontFamily: "--pollen-heading-font-family",
} as const;

/**
 * Default colors for this bundle's visual identity.
 * Used as CSS variable fallbacks when the API omits tenant colors.
 */
export const DEFAULT_WIDGET_COLORS = {
  textColor: "#000A1E",
  backgroundColor: "#F2F4F7",
  primaryColor: "#002263",
  cardBackgroundColor: "#FFFFFF",
  textSecondaryColor: "#737373",
  borderColor: "#BFC8D8",
  accentColor: "#FF5300",
} as const;

// Alias for internal use
const DEFAULTS = DEFAULT_WIDGET_COLORS;

// Tenant UI Widget Base Color Palette
export const colors = {
  // ===== TENANT-CONFIGURABLE COLORS =====
  // These use CSS variables that are set from the API response

  // Primary text color
  text: `var(${CSS_VAR_NAMES.textColor}, ${DEFAULTS.textColor})`,

  // Main surface/background color
  surface: `var(${CSS_VAR_NAMES.backgroundColor}, ${DEFAULTS.backgroundColor})`,

  // Accent color for buttons, links, CTAs, headers
  primary: `var(${CSS_VAR_NAMES.primaryColor}, ${DEFAULTS.primaryColor})`,

  // Elevated surfaces: inputs, cards, modals
  background: `var(${CSS_VAR_NAMES.cardBackgroundColor}, ${DEFAULTS.cardBackgroundColor})`,

  // Muted/secondary text
  textSecondary: `var(${CSS_VAR_NAMES.textSecondaryColor}, ${DEFAULTS.textSecondaryColor})`,

  // Borders and dividers
  border: `var(${CSS_VAR_NAMES.borderColor}, ${DEFAULTS.borderColor})`,

  // ===== DERIVED/COMPUTED COLORS =====
  // These are computed from the primary color or remain static

  // Primary hover state (slightly darker than primary)
  primaryHover: `color-mix(in srgb, var(${CSS_VAR_NAMES.primaryColor}, ${DEFAULTS.primaryColor}) 85%, black)`,

  // Surface hover state
  surfaceHover: `color-mix(in srgb, var(${CSS_VAR_NAMES.backgroundColor}, ${DEFAULTS.backgroundColor}) 80%, var(${CSS_VAR_NAMES.borderColor}, ${DEFAULTS.borderColor}))`,

  // Surface secondary
  surfaceSecondary: `color-mix(in srgb, var(${CSS_VAR_NAMES.backgroundColor}, ${DEFAULTS.backgroundColor}) 70%, var(${CSS_VAR_NAMES.borderColor}, ${DEFAULTS.borderColor}))`,

  // Tertiary text (slightly muted from secondary)
  textTertiary: `var(${CSS_VAR_NAMES.textSecondaryColor}, ${DEFAULTS.textSecondaryColor})`,

  // Neutral grey for subtle UI elements (uses secondary text)
  neutralGrey: `var(${CSS_VAR_NAMES.textSecondaryColor}, ${DEFAULTS.textSecondaryColor})`,

  // Muted text (alias for textSecondary)
  muted: `var(${CSS_VAR_NAMES.textSecondaryColor}, ${DEFAULTS.textSecondaryColor})`,

  // ===== STATIC ACCENT COLORS =====
  // These don't change with theme

  accent: `var(${CSS_VAR_NAMES.accentColor}, ${DEFAULTS.accentColor})`, // Base accent (orange)
  yellow: "#FFBE40", // Accent yellow
  green: "#00EF97", // Accent green
  teal: "#00595A", // Accent teal
  pink: "#F576ED", // Accent pink
  blue: "#4B4BEE", // Accent blue
  grey: "#DBE6E6", // Accent grey
  purple: "#8781E2", // Accent purple
  softPurple: "#B3AFED", // Accent soft purple

  // ===== SYSTEM COLORS =====
  danger: "#FF0000", // Error/danger color
};

/**
 * System font stack that works beautifully across all platforms:
 * - macOS/iOS: San Francisco (-apple-system)
 * - Windows: Segoe UI
 * - Android/Chrome OS: Roboto
 * - Linux: Noto Sans / Liberation Sans
 * - Fallbacks: Helvetica Neue, Arial, sans-serif
 * - Emoji support for all platforms
 */
export const BODY_FONT_STACK = `
  -apple-system,
  BlinkMacSystemFont,
  "Segoe UI",
  Roboto,
  "Helvetica Neue",
  Arial,
  "Noto Sans",
  "Liberation Sans",
  sans-serif,
  "Apple Color Emoji",
  "Segoe UI Emoji",
  "Segoe UI Symbol",
  "Noto Color Emoji"
`
  .replaceAll(/\s+/g, " ")
  .trim();

export const HEADING_FONT_STACK = BODY_FONT_STACK;

export const fonts = {
  headingFont: css`
    font-family: var(${CSS_VAR_NAMES.headingFontFamily}, ${HEADING_FONT_STACK});
  `,
  bodyFont: css`
    font-family: var(${CSS_VAR_NAMES.fontFamily}, ${BODY_FONT_STACK});
  `,
};

export const radius = {
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  full: 9999,
};

export const shadows = {
  // Accent shadows for primary interactive elements
  accentShadow: {
    default: css`
      box-shadow: 0px 0px 11px 0px rgba(8, 48, 229, 0.3);
    `,
    hover: css`
      box-shadow: 0px 2px 15px 1px rgba(8, 48, 229, 0.35);
    `,
    active: css`
      box-shadow: 0px 0px 8px 0px rgba(8, 48, 229, 0.25);
    `,
  },

  // General purpose shadows
  main: css`
    box-shadow:
      0px 8px 32px rgba(0, 0, 0, 0.08),
      0px 1px 2px rgba(0, 0, 0, 0.05);
  `,
  searchBox: css`
    box-shadow:
      0px 0px 12px 0px rgba(79, 187, 195, 0.3),
      0px 4px 20px 0px rgba(17, 17, 17, 0.12);
  `,
};

export const gradients = {
  buttonBackground: css`
    background:
      linear-gradient(0deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.9) 100%),
      linear-gradient(0deg, #f6feff 0%, #f6feff 100%), #d9f4f6;
  `,
  headerBackground: css`
    background: linear-gradient(
      148deg,
      ${colors.blue} -123.99%,
      var(${CSS_VAR_NAMES.primaryColor}, ${DEFAULTS.primaryColor}) 99.32%
    );
  `,
};

export const borders = {
  button: css`
    border: 1px solid rgba(79, 187, 195, 0.25);
  `,
  gradientBlue: css`
    border: 1.5px solid transparent;
    background:
      linear-gradient(white, white) padding-box,
      linear-gradient(160deg, ${colors.purple} 0%, ${colors.purple} 5%, white 80%, white 100%) border-box;
  `,
};

export const spacing = {
  "xs": 4,
  "sm": 8,
  "md": 12,
  "lg": 16,
  "xl": 20,
  "2xl": 24,
  "3xl": 32,
  "4xl": 40,
  "5xl": 48,
};

export const breakpoints = {
  "sm": 640,
  "md": 768,
  "lg": 1024,
  "xl": 1280,
  "2xl": 1536,
};

export const media = {
  "sm": `@media (min-width: ${breakpoints.sm}px)`,
  "md": `@media (min-width: ${breakpoints.md}px)`,
  "lg": `@media (min-width: ${breakpoints.lg}px)`,
  "xl": `@media (min-width: ${breakpoints.xl}px)`,
  "2xl": `@media (min-width: ${breakpoints["2xl"]}px)`,
};

export const backgrounds = {
  glassy: css`
    background: color-mix(
      in srgb,
      var(${CSS_VAR_NAMES.cardBackgroundColor}, ${DEFAULTS.cardBackgroundColor}) 80%,
      transparent
    );
    backdrop-filter: blur(6px);
  `,
  overlay: css`
    background: color-mix(in srgb, var(${CSS_VAR_NAMES.textColor}, ${DEFAULTS.textColor}) 50%, transparent);
  `,
};

export const iconGradients = {
  sparkle: css`
    background: linear-gradient(138deg, #d9f4f6 -6.73%, ${colors.accent} 125%);
    border-radius: ${radius.sm}px;
  `,
};

export const resetButton = css`
  border-radius: ${radius.sm}px;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  margin: 0;
  font-size: 16px;
  font-weight: 500;
`;

export const glassyButton = css`
  border-radius: ${radius.lg}px;
  ${borders.button}
  ${gradients.buttonBackground}
  ${shadows.main}
  ${backgrounds.glassy}
  color: ${colors.text};
  font-size: 16px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.md}px;
  padding: ${spacing.lg}px;
  cursor: pointer;
  outline: none;
  transition: all 200ms ease-in-out;

  &:hover {
    background:
      linear-gradient(0deg, rgba(79, 187, 195, 0.08) 0%, rgba(79, 187, 195, 0.08) 100%),
      linear-gradient(0deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.9) 100%),
      linear-gradient(0deg, #f6feff 0%, #f6feff 100%), #d9f4f6;
    box-shadow:
      0px 0px 16px 0px rgba(79, 187, 195, 0.4),
      0px 6px 24px 0px rgba(17, 17, 17, 0.15);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0px);
  }
`;

// Animation speed constants
export const ANIMATION_SPEEDS = {
  FAST: 200,
  STANDARD: 300,
  SLOW: 500,
} as const;

export const transitions = {
  // Unified animation functions (all default to STANDARD speed)
  fadeSlide: (duration: number = ANIMATION_SPEEDS.STANDARD) => css`
    transition:
      opacity ${duration}ms ease-in-out,
      transform ${duration}ms ease-in-out;
  `,

  fadeOnly: (duration: number = ANIMATION_SPEEDS.STANDARD) => css`
    transition: opacity ${duration}ms ease-in-out;
  `,

  pulse: (duration: number = ANIMATION_SPEEDS.SLOW) => css`
    animation: pulse ${duration}ms ease-in-out infinite alternate;

    @keyframes pulse {
      0% {
        opacity: 0.5;
      }
      100% {
        opacity: 1;
      }
    }
  `,
};

// Hide scrollbars across all browsers
export const hideScrollbar = css`
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`;
